import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
  Tabs,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  FileTextOutlined,
  CarOutlined,
  UngroupOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getPaymentStatisticsByZone } from "../../../api/statistics/statisticsRequest";
import { getZones } from "../../../api/zone/zoneRequest";
import { formatAmountInFrench } from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;
const { TabPane } = Tabs;

const PaymentsByZoneStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zones, setZones] = useState([]);
  const [paymentsData, setPaymentsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchZones();
  }, []);

  const filterPaymentStatistics = async () => {
    setLoading(true);

    try {
      const data = await getPaymentStatisticsByZone({
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD")
            : null,
        zone_id: selectedZone,
      });
      setPaymentsData(data);
    } catch (error) {
      setPaymentsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const data = await getZones();
      setZones(data.data);
    } catch (error) {
      message.error("Échec de la récupération des zones");
    }
  };

  // Chart configurations
  const summaryChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Revenus Totaux par Zone",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: paymentsData?.summary.map((zone) => zone.zone_name) || [],
      crosshair: true,
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: "Montant Total",
        },
      },
      {
        min: 0,
        title: {
          text: "Nombre de Transactions",
        },
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      backgroundColor: "rgba(255,255,255,0.8)",
      borderColor: "#CCC",
      borderWidth: 1,
      shadow: false,
    },
    series: [
      {
        name: "Montant Total",
        data: paymentsData?.summary.map((zone) =>
          parseFloat(zone.total_amount)
        ) || [],
        yAxis: 0,
        tooltip: {
          valueDecimals: 2,
          valuePrefix: "",
          valueSuffix: " DH",
        },
      },
      {
        name: "Nombre de Transactions",
        data: paymentsData?.summary.map((zone) => zone.total_transactions) || [],
        yAxis: 1,
      },
    ],
  };

  const infractionPaymentsPieOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Paiements d'Infractions par Zone",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat:
        "Paiements: <b>{point.y}</b><br>Montant: <b>{point.total_amount}</b>",
    },
    series: [
      {
        name: "Infractions",
        data: paymentsData
          ? paymentsData.infraction_payments_by_zone.map((item) => ({
              name: item.zone_name,
              y: item.total_payments,
              total_amount: formatAmountInFrench(parseFloat(item.total_amount)),
            }))
          : [],
      },
    ],
  };

  const ticketParkingPieOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Transactions de Parking par Zone",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat:
        "Transactions: <b>{point.y}</b><br>Montant: <b>{point.total_amount}</b>",
    },
    series: [
      {
        name: "Parking",
        data: paymentsData
          ? paymentsData.ticket_parking_payments_by_zone.map((item) => ({
              name: item.zone_name,
              y: item.total_transactions,
              total_amount: formatAmountInFrench(parseFloat(item.total_amount)),
            }))
          : [],
      },
    ],
  };

  const subscriptionPieOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Paiements d'Abonnements par Zone",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat:
        "Paiements: <b>{point.y}</b><br>Montant: <b>{point.total_amount}</b>",
    },
    series: [
      {
        name: "Abonnements",
        data: paymentsData
          ? paymentsData.subscription_payments_by_zone.map((item) => ({
              name: item.zone_name,
              y: item.total_payments,
              total_amount: formatAmountInFrench(parseFloat(item.total_amount)),
            }))
          : [],
      },
    ],
  };

  // Calculate totals for statistics
  const totalRevenue = paymentsData?.summary.reduce(
    (sum, zone) => sum + parseFloat(zone.total_amount),
    0
  ) || 0;

  const totalTransactions = paymentsData?.summary.reduce(
    (sum, zone) => sum + zone.total_transactions,
    0
  ) || 0;

  const totalInfractionAmount = paymentsData?.infraction_payments_by_zone.reduce(
    (sum, zone) => sum + parseFloat(zone.total_amount),
    0
  ) || 0;

  const totalTicketParkingAmount = paymentsData?.ticket_parking_payments_by_zone.reduce(
    (sum, zone) => sum + parseFloat(zone.total_amount),
    0
  ) || 0;

  const totalSubscriptionAmount = paymentsData?.subscription_payments_by_zone.reduce(
    (sum, zone) => sum + parseFloat(zone.total_amount),
    0
  ) || 0;

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  placeholder={["Date début", "Date fin"]}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Sélectionner une Zone"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedZone(value)}
                  allowClear
                >
                  {zones.map((zone) => (
                    <Option key={zone.id} value={zone.id}>
                      {zone.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterPaymentStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {paymentsData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des Revenus par Zone
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Revenus Totaux"
                    value={totalRevenue.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="DH"
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Transactions"
                    value={totalTransactions}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Revenus Infractions"
                    value={totalInfractionAmount.toFixed(2)}
                    prefix={<WarningOutlined />}
                    suffix="DH"
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Revenus Parking"
                    value={totalTicketParkingAmount.toFixed(2)}
                    prefix={<CarOutlined />}
                    suffix="DH"
                  />
                </Col>
              </Row>
              <Row gutter={16} className="mb-6" justify="center">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Revenus Abonnements"
                    value={totalSubscriptionAmount.toFixed(2)}
                    prefix={<UngroupOutlined />}
                    suffix="DH"
                  />
                </Col>
              </Row>
            </Card>

            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={summaryChartOptions}
              />
            </Card>

            <Tabs defaultActiveKey="1" className="shadow-sm">
              <TabPane tab="Paiements d'Infractions" key="1">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={infractionPaymentsPieOptions}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <Title level={4} className="mb-4">
                        Détails des Paiements d'Infractions
                      </Title>
                      <Table
                        dataSource={paymentsData?.infraction_payments_by_zone}
                        columns={[
                          {
                            title: "Zone",
                            dataIndex: "zone_name",
                            key: "zone_name",
                          },
                          {
                            title: "Paiements",
                            dataIndex: "total_payments",
                            key: "total_payments",
                            align: "center",
                            sorter: (a, b) => a.total_payments - b.total_payments,
                          },
                          {
                            title: "Montant Total",
                            dataIndex: "total_amount",
                            key: "total_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.total_amount) - parseFloat(b.total_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                          {
                            title: "Montant Moyen",
                            dataIndex: "average_amount",
                            key: "average_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.average_amount) - parseFloat(b.average_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                        ]}
                        pagination={false}
                      />
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Transactions de Parking" key="2">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={ticketParkingPieOptions}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <Title level={4} className="mb-4">
                        Détails des Transactions de Parking
                      </Title>
                      <Table
                        dataSource={paymentsData?.ticket_parking_payments_by_zone}
                        columns={[
                          {
                            title: "Zone",
                            dataIndex: "zone_name",
                            key: "zone_name",
                          },
                          {
                            title: "Transactions",
                            dataIndex: "total_transactions",
                            key: "total_transactions",
                            align: "center",
                            sorter: (a, b) => a.total_transactions - b.total_transactions,
                          },
                          {
                            title: "Montant Total",
                            dataIndex: "total_amount",
                            key: "total_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.total_amount) - parseFloat(b.total_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                          {
                            title: "Montant Moyen",
                            dataIndex: "average_amount",
                            key: "average_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.average_amount) - parseFloat(b.average_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                        ]}
                        pagination={false}
                      />
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Paiements d'Abonnements" key="3">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={subscriptionPieOptions}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card className="shadow-sm">
                      <Title level={4} className="mb-4">
                        Détails des Paiements d'Abonnements
                      </Title>
                      <Table
                        dataSource={paymentsData?.subscription_payments_by_zone}
                        columns={[
                          {
                            title: "Zone",
                            dataIndex: "zone_name",
                            key: "zone_name",
                          },
                          {
                            title: "Paiements",
                            dataIndex: "total_payments",
                            key: "total_payments",
                            align: "center",
                            sorter: (a, b) => a.total_payments - b.total_payments,
                          },
                          {
                            title: "Montant Total",
                            dataIndex: "total_amount",
                            key: "total_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.total_amount) - parseFloat(b.total_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                          {
                            title: "Montant Moyen",
                            dataIndex: "average_amount",
                            key: "average_amount",
                            align: "right",
                            sorter: (a, b) =>
                              parseFloat(a.average_amount) - parseFloat(b.average_amount),
                            render: (text) =>
                              `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                          },
                        ]}
                        pagination={false}
                      />
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Résumé Global" key="4">
                <Card className="shadow-sm">
                  <Title level={3} className="mb-4">
                    Résumé Global par Zone
                  </Title>
                  <Table
                    dataSource={paymentsData?.summary}
                    columns={[
                      { title: "Zone", dataIndex: "zone_name", key: "zone_name" },
                      {
                        title: "Revenus Totaux",
                        dataIndex: "total_amount",
                        key: "total_amount",
                        align: "right",
                        sorter: (a, b) =>
                          parseFloat(a.total_amount) - parseFloat(b.total_amount),
                        render: (text) =>
                          `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                      },
                      {
                        title: "Total Transactions",
                        dataIndex: "total_transactions",
                        key: "total_transactions",
                        align: "center",
                        sorter: (a, b) => a.total_transactions - b.total_transactions,
                      },
                      {
                        title: "Revenus Infractions",
                        dataIndex: "infraction_amount",
                        key: "infraction_amount",
                        align: "right",
                        sorter: (a, b) =>
                          parseFloat(a.infraction_amount) - parseFloat(b.infraction_amount),
                        render: (text) =>
                          `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                      },
                      {
                        title: "Revenus Parking",
                        dataIndex: "ticket_parking_amount",
                        key: "ticket_parking_amount",
                        align: "right",
                        sorter: (a, b) =>
                          parseFloat(a.ticket_parking_amount) - parseFloat(b.ticket_parking_amount),
                        render: (text) =>
                          `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                      },
                      {
                        title: "Revenus Abonnements",
                        dataIndex: "subscription_amount",
                        key: "subscription_amount",
                        align: "right",
                        sorter: (a, b) =>
                          parseFloat(a.subscription_amount) - parseFloat(b.subscription_amount),
                        render: (text) =>
                          `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
                      },
                    ]}
                    pagination={false}
                    scroll={{ x: 800 }}
                  />
                </Card>
              </TabPane>
            </Tabs>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default PaymentsByZoneStatistics;
